import { configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import { AppState, type AppStateStatus, Platform } from "react-native";

import { selectedChildReducer } from "@/features/selectedChild/selectedChildSlice";
import { s4pApi } from "@/query/apiSlice";

export const store = configureStore({
    reducer: {
        [s4pApi.reducerPath]: s4pApi.reducer,
        selectedChild: selectedChildReducer,
    },
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware().concat(s4pApi.middleware),
});

// Native: refetch subscribed queries when the app returns to the foreground.
// Web: do not listen to window focus — switching tabs/apps would reload everything.
// Notification taps invalidate specific tags in navigateFromNotificationData.
// Reconnect refetch still runs on web via `online` / `offline`.
setupListeners(
    store.dispatch,
    (_dispatch, { onFocus, onFocusLost, onOnline, onOffline }) => {
        if (Platform.OS === "web") {
            const handleOnline = () => onOnline();
            const handleOffline = () => onOffline();
            window.addEventListener("online", handleOnline);
            window.addEventListener("offline", handleOffline);
            return () => {
                window.removeEventListener("online", handleOnline);
                window.removeEventListener("offline", handleOffline);
            };
        }

        const onAppStateChange = (status: AppStateStatus) => {
            if (status === "active") {
                onFocus();
            } else {
                onFocusLost();
            }
        };

        const subscription = AppState.addEventListener(
            "change",
            onAppStateChange,
        );

        return () => {
            subscription.remove();
        };
    },
);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
